const MODAL_SELECTOR = '[data-showroom-modal]';
const TRIGGER_SELECTOR = '[data-showroom-modal-trigger]';
const CLOSE_SELECTOR = '[data-showroom-modal-close]';

const focusableSelectors = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
];

export function initShowroomModal() {
  const modal = document.querySelector(MODAL_SELECTOR);

  if (!modal || modal.dataset.showroomModalBound === 'true') {
    return;
  }

  const dialog = modal.querySelector('[role="dialog"]');
  const closeButton = modal.querySelector(CLOSE_SELECTOR);
  let lastTrigger = null;

  modal.dataset.showroomModalBound = 'true';

  const getFocusableElements = () =>
    Array.from(dialog?.querySelectorAll(focusableSelectors.join(',')) ?? []).filter(
      (element) => element.offsetParent !== null || element === document.activeElement,
    );

  const focusFirstElement = () => {
    const focusables = getFocusableElements();
    const target = focusables[0] || closeButton;

    target?.focus?.();
  };

  const openModal = (trigger) => {
    lastTrigger = trigger instanceof HTMLElement ? trigger : null;
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('modal-open');

    window.requestAnimationFrame(() => {
      focusFirstElement();
    });
  };

  const closeModal = () => {
    if (!modal.classList.contains('is-open')) {
      return;
    }

    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('modal-open');

    window.requestAnimationFrame(() => {
      lastTrigger?.focus?.();
      lastTrigger = null;
    });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.(TRIGGER_SELECTOR);

    if (trigger) {
      event.preventDefault();
      openModal(trigger);
      return;
    }

    if (event.target.closest?.(CLOSE_SELECTOR) && modal.classList.contains('is-open')) {
      event.preventDefault();
      closeModal();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !modal.classList.contains('is-open')) {
      return;
    }

    closeModal();
  });

  modal.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab' || !modal.classList.contains('is-open')) {
      return;
    }

    const focusables = getFocusableElements();
    if (focusables.length === 0) {
      event.preventDefault();
      closeButton?.focus?.();
      return;
    }

    const firstFocusable = focusables[0];
    const lastFocusable = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
      return;
    }

    if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal && modal.classList.contains('is-open')) {
      closeModal();
    }
  });
}
